import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  requestAlarmGroups,
  requestAlarmGroup,
  createAlarmGroupRequest,
  updateAlarmGroupRequest,
  deleteAlarmGroupRequest,
  ICreateAlarmGroupDTO,
  IUpdateAlarmGroupDTO,
} from '_/api/alarmGroups/alarmGroups.api';
import { notification } from 'antd';

// Query keys
export const ALARM_GROUPS_QUERY_KEY = (flowId: string): string[] => ['flow', flowId, 'alarmGroups'];
export const ALARM_GROUP_QUERY_KEY = (alarmGroupId: string): string[] => ['alarmGroup', alarmGroupId];

/**
 * Hook to fetch alarm groups for a flow
 */
export const useAlarmGroups = (flowId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ALARM_GROUPS_QUERY_KEY(flowId || ''),
    queryFn: () => requestAlarmGroups(flowId!),
    enabled: !!flowId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single alarm group
 */
export const useAlarmGroup = (alarmGroupId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ALARM_GROUP_QUERY_KEY(alarmGroupId || ''),
    queryFn: () => requestAlarmGroup(alarmGroupId!),
    enabled: !!alarmGroupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create an alarm group
 */
export const useCreateAlarmGroup = (flowId?: string): UseMutationResult<any, Error, ICreateAlarmGroupDTO> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alarmGroupData: ICreateAlarmGroupDTO) => createAlarmGroupRequest(flowId!, alarmGroupData),
    onSuccess: () => {
      // Invalidate alarm groups list
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_GROUPS_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Alarm Group Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error creating alarm group', error);
      notification.error({
        message: error.message || 'Error creating alarm group',
      });
    },
  });
};

/**
 * Hook to update an alarm group
 */
export const useUpdateAlarmGroup = (
  alarmGroupId?: string,
  flowId?: string,
): UseMutationResult<any, Error, IUpdateAlarmGroupDTO> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alarmGroupData: IUpdateAlarmGroupDTO) => updateAlarmGroupRequest(alarmGroupId!, alarmGroupData),
    onSuccess: () => {
      // Invalidate the specific alarm group query
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_GROUP_QUERY_KEY(alarmGroupId) });
      }
      // Invalidate the alarm groups list
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_GROUPS_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Alarm Group Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating alarm group', error);
      notification.error({
        message: error.message || 'Error updating alarm group',
      });
    },
  });
};

/**
 * Hook to delete an alarm group
 */
export const useDeleteAlarmGroup = (flowId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alarmGroupId: string) => deleteAlarmGroupRequest(alarmGroupId),
    onSuccess: () => {
      // Invalidate alarm groups list
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: ALARM_GROUPS_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Alarm Group Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting alarm group', error);
      notification.error({
        message: error.message || 'Error deleting alarm group',
      });
    },
  });
};
